import { FormError } from '@/components/ui/form-error';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NotificationPreferencesPanel } from '@/components/notifications/NotificationPreferencesPanel';
import { ProfileSidebar } from '@/components/profile/ProfileSidebar';
import { refreshAuthProfileCache } from '@/hooks/queries/auth.queries';
import {
  useCancelProfileBooking,
  useConfirmProfileBooking,
  useProfileBookings,
} from '@/hooks/queries/useProfileBookings';
import { useCancelProfileWalkIn, useProfileWalkIns } from '@/hooks/queries/useProfileWalkIns';
import { useFavorites } from '@/hooks/useFavorites';
import { useAuth } from '@/hooks/useAuth';
import { getApiErrorMessage } from '@/lib/api/error';
import { resolveCompanyProfile } from '@/lib/image-placeholders';
import { buildProfileUpdatePayload, getEditableProfileValues } from '@/lib/profile';
import {
  canCancelProfileBooking,
  canConfirmProfileBooking,
  getProfileBookingDisplayState,
  getProfileBookingStatusLabel,
} from '@/lib/profile-bookings';
import {
  canCancelProfileWalkIn,
  getProfileWalkInDisplayState,
  getProfileWalkInStatusLabel,
} from '@/lib/profile-walk-ins';
import { getCPFDigits, isValidCPF } from '@/lib/cpf';
import { formatPhoneNumber, isPhoneNumberComplete } from '@/lib/phoneNumber';
import { getUserDisplayName, getUserInitials } from '@/lib/user';
import type {
  BookingFilter,
  EditableProfileValues,
  FavoriteShopSnapshot,
  ProfileBooking,
  ProfileTab,
} from '@/types/profile';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { format, isToday, isTomorrow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  ArrowLeft,
  Camera,
  Calendar,
  CalendarClock,
  ChevronRight,
  Clock,
  Heart,
  Loader2,
  LogOut,
  Mail,
  MapPin,
  Phone,
  Save,
  Star,
  User,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type ChangeEvent, type KeyboardEvent } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useMaskInput } from 'use-mask-input';
import { z } from 'zod';
import { AuthService } from '@/services/auth.service';

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const MAX_PROFILE_PHOTO_SIZE_BYTES = 5 * 1024 * 1024;
const ACCEPTED_PROFILE_PHOTO_TYPES = new Set(['image/jpeg', 'image/jpg', 'image/png', 'image/webp']);

const profileSchema = z.object({
  birthDate: z.string().refine((value) => value === '' || new Date(`${value}T00:00:00`).getTime() <= Date.now(), {
    message: 'Informe uma data válida',
  }),
  email: z.string().trim().email('Informe um email válido').or(z.literal('')),
  name: z.string().trim().min(2, 'Informe seu nome'),
  phoneNumber: z
    .string()
    .trim()
    .refine((value) => value === '' || isPhoneNumberComplete(value), {
      message: 'Informe um telefone válido',
    }),
  socialNumber: z
    .string()
    .trim()
    .refine(
      (value) => {
        const digits = getCPFDigits(value);

        return digits.length === 0 || (digits.length === 11 && isValidCPF(digits));
      },
      {
        message: 'Informe um CPF válido',
      },
    ),
});

function getBookingDateTime(booking: ProfileBooking) {
  const bookingDateTime = new Date(`${booking.date}T${booking.time || '00:00'}:00`);

  if (Number.isNaN(bookingDateTime.getTime())) {
    return null;
  }

  return bookingDateTime;
}

function formatBookingDateLabel(booking: ProfileBooking) {
  const bookingDateTime = getBookingDateTime(booking);

  if (!bookingDateTime) {
    return booking.date;
  }

  if (isToday(bookingDateTime)) {
    return 'Hoje';
  }

  if (isTomorrow(bookingDateTime)) {
    return 'Amanhã';
  }

  return format(bookingDateTime, 'dd/MM/yyyy', { locale: ptBR });
}

function formatBookingSummary(booking: ProfileBooking) {
  const serviceNames = booking.services.flatMap((service) => (service.name ? [service.name] : []));

  if (serviceNames.length === 0) {
    return 'Horário agendado';
  }

  return serviceNames.join(', ');
}

function getFavoriteShopName(shop: FavoriteShopSnapshot) {
  return shop.name?.trim() || 'Local salvo';
}

function resolveProfileTab(value?: string | null): ProfileTab {
  if (value === 'bookings' || value === 'favorites' || value === 'preferences') {
    return value;
  }

  return 'details';
}

function formatDateTimeLabel(value?: string | null) {
  if (!value) {
    return 'Agora';
  }

  const parsedValue = new Date(value);

  if (Number.isNaN(parsedValue.getTime())) {
    return 'Agora';
  }

  if (isToday(parsedValue)) {
    return `Hoje às ${format(parsedValue, 'HH:mm', { locale: ptBR })}`;
  }

  if (isTomorrow(parsedValue)) {
    return `Amanhã às ${format(parsedValue, 'HH:mm', { locale: ptBR })}`;
  }

  return format(parsedValue, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
}

const Profile = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { favoriteItems, toggleFavorite, totalFavorites } = useFavorites();
  const { isLoading, logout, user } = useAuth();
  const [tab, setTab] = useState<ProfileTab>(() => resolveProfileTab(searchParams.get('tab')));
  const [bookingFilter, setBookingFilter] = useState<BookingFilter>('upcoming');
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [bookingActionInFlight, setBookingActionInFlight] = useState<{
    action: 'cancel' | 'confirm';
    bookingId: string;
  } | null>(null);
  const [walkInActionInFlight, setWalkInActionInFlight] = useState<string | null>(null);
  const phoneMaskRef = useMaskInput({ mask: ['(99) 9999-9999', '(99) 99999-9999'] });
  const socialNumberMaskRef = useMaskInput({ mask: '999.999.999-99' });
  const photoInputRef = useRef<HTMLInputElement | null>(null);

  const initialProfileValues = useMemo(() => getEditableProfileValues(user), [user]);
  const {
    register,
    reset,
    handleSubmit,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<EditableProfileValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: initialProfileValues,
  });
  const phoneNumberField = register('phoneNumber');
  const socialNumberField = register('socialNumber');
  const canEditSocialNumber = getCPFDigits(user?.socialNumber).length === 0;

  useEffect(() => {
    reset(initialProfileValues);
  }, [initialProfileValues, reset]);

  useEffect(() => {
    const nextTab = resolveProfileTab(searchParams.get('tab'));

    setTab((currentTab) => (currentTab === nextTab ? currentTab : nextTab));
  }, [searchParams]);

  const upcomingBookingsQuery = useProfileBookings('upcoming', user?.id, Boolean(user));
  const historyBookingsQuery = useProfileBookings('history', user?.id, Boolean(user));
  const walkInsQuery = useProfileWalkIns(user?.id, Boolean(user));
  const { confirmBooking } = useConfirmProfileBooking(user?.id);
  const { cancelBooking } = useCancelProfileBooking(user?.id);
  const { cancelWalkIn } = useCancelProfileWalkIn(user?.id);
  const upcomingBookings = upcomingBookingsQuery.bookings;
  const historyBookings = historyBookingsQuery.bookings;
  const activeWalkIns = walkInsQuery.activeWalkIns;
  const bookingCounters =
    upcomingBookingsQuery.counters.upcoming > 0 || upcomingBookingsQuery.counters.history > 0
      ? upcomingBookingsQuery.counters
      : historyBookingsQuery.counters;
  const visibleBookings = bookingFilter === 'upcoming' ? upcomingBookings : historyBookings;
  const isLoadingVisibleBookings =
    bookingFilter === 'upcoming' ? upcomingBookingsQuery.isLoading : historyBookingsQuery.isLoading;
  const favoriteShops = useMemo(
    () =>
      favoriteItems.toSorted((left, right) => {
        const leftName = getFavoriteShopName(left);
        const rightName = getFavoriteShopName(right);

        return leftName.localeCompare(rightName, 'pt-BR');
      }),
    [favoriteItems],
  );
  const nextBooking = upcomingBookings[0] ?? null;
  const nextWalkIn = activeWalkIns[0] ?? null;
  const displayName = getUserDisplayName(user);
  const contactLabel = formatPhoneNumber(user?.phoneNumber) || user?.email || 'Conta autenticada';

  const changeTab = (nextTab: ProfileTab) => {
    setTab(nextTab);
    setSearchParams(
      (currentParams) => {
        const nextParams = new URLSearchParams(currentParams);

        if (nextTab === 'details') {
          nextParams.delete('tab');
        } else {
          nextParams.set('tab', nextTab);
        }

        return nextParams;
      },
      { replace: true },
    );
  };

  const openPhotoPicker = () => {
    if (isUploadingPhoto || isSubmitting) {
      return;
    }

    photoInputRef.current?.click();
  };

  const handleLogout = async () => {
    if (isLoggingOut) {
      return;
    }

    setIsLoggingOut(true);

    try {
      await logout();
      navigate('/', { replace: true });
    } catch {
      toast.error('Não foi possível sair da conta', {
        description: 'Tente novamente em instantes.',
      });
    } finally {
      setIsLoggingOut(false);
    }
  };

  const handleSaveProfile = async (values: EditableProfileValues) => {
    if (!user) {
      toast.error('Não foi possível salvar', {
        description: 'Entre novamente para continuar.',
      });
      return;
    }

    try {
      const updatedUser = await AuthService.updateProfile(buildProfileUpdatePayload(values));
      const syncedUser = await refreshAuthProfileCache(queryClient, updatedUser ?? undefined);
      const nextUser = syncedUser ?? updatedUser ?? user;

      reset(getEditableProfileValues(nextUser));

      toast.success('Dados salvos', {
        description: 'Seu perfil foi atualizado com sucesso.',
      });
    } catch (error) {
      toast.error('Não foi possível salvar', {
        description: getApiErrorMessage(error, 'Tente novamente em instantes.'),
      });
    }
  };

  const handlePhotoSelection = async (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];

    event.target.value = '';

    if (!selectedFile) {
      return;
    }

    if (!ACCEPTED_PROFILE_PHOTO_TYPES.has(selectedFile.type)) {
      toast.error('Formato não suportado', {
        description: 'Use uma imagem JPG, JPEG, PNG ou WEBP.',
      });
      return;
    }

    if (selectedFile.size > MAX_PROFILE_PHOTO_SIZE_BYTES) {
      toast.error('Arquivo muito grande', {
        description: 'Escolha uma imagem de até 5 MB.',
      });
      return;
    }

    try {
      setIsUploadingPhoto(true);

      const updatedUser = await AuthService.uploadProfilePhoto(selectedFile);

      await refreshAuthProfileCache(queryClient, updatedUser ?? undefined);

      toast.success('Foto atualizada', {
        description: 'Sua nova foto já está disponível no perfil.',
      });
    } catch (error) {
      toast.error('Não foi possível atualizar a foto', {
        description: getApiErrorMessage(error, 'Tente novamente em instantes.'),
      });
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const handleConfirmBooking = async (bookingId: string) => {
    try {
      setBookingActionInFlight({ action: 'confirm', bookingId });
      await confirmBooking(bookingId);

      toast.success('Agendamento confirmado', {
        description: 'Seu horário foi confirmado com sucesso.',
      });
    } catch (error) {
      toast.error('Não foi possível confirmar o agendamento', {
        description: getApiErrorMessage(error, 'Tente novamente em instantes.'),
      });
    } finally {
      setBookingActionInFlight(null);
    }
  };

  const handleCancelBooking = async (bookingId: string) => {
    try {
      setBookingActionInFlight({ action: 'cancel', bookingId });
      await cancelBooking(bookingId);

      toast.success('Agendamento cancelado', {
        description: 'Seu horário foi atualizado com sucesso.',
      });
    } catch (error) {
      toast.error('Não foi possível cancelar o agendamento', {
        description: getApiErrorMessage(error, 'Tente novamente em instantes.'),
      });
    } finally {
      setBookingActionInFlight(null);
    }
  };

  const openBookingManagement = (bookingId: string) => {
    navigate(`/agendamentos/${bookingId}`);
  };

  const handleBookingCardKeyDown = (event: KeyboardEvent<HTMLElement>, bookingId: string) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    openBookingManagement(bookingId);
  };

  const handleCancelWalkIn = async (walkInId: string) => {
    try {
      setWalkInActionInFlight(walkInId);
      await cancelWalkIn(walkInId);

      toast.success('Saída da fila confirmada', {
        description: 'Sua fila presencial foi cancelada com sucesso.',
      });
    } catch (error) {
      toast.error('Não foi possível sair da fila', {
        description: getApiErrorMessage(error, 'Tente novamente em instantes.'),
      });
    } finally {
      setWalkInActionInFlight(null);
    }
  };

  if (isLoading && !user) {
    return (
      <div className="bg-background min-h-screen">
        <header className="border-border bg-background/95 sticky top-0 z-40 border-b backdrop-blur-sm">
          <div className="container flex h-14 items-center gap-3">
            <button
              type="button"
              aria-label="Voltar"
              onClick={() => navigate(-1)}
              className="hover:bg-muted -ml-2 flex h-10 w-10 items-center justify-center rounded-lg transition-colors"
            >
              <ArrowLeft className="text-foreground h-5 w-5" />
            </button>
            <h1 className="text-foreground flex-1 text-center text-base font-semibold">Meu perfil</h1>
            <div className="h-10 w-10 shrink-0" aria-hidden="true" />
          </div>
        </header>

        <div className="container mx-auto max-w-5xl py-6">
          <Card className="border-border">
            <CardContent className="text-muted-foreground flex items-center gap-3 p-6 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
              Carregando seu perfil...
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background min-h-screen">
      <header className="border-border bg-background/95 sticky top-0 z-40 border-b backdrop-blur-sm">
        <div className="container flex h-14 items-center gap-3">
          <button
            type="button"
            aria-label="Voltar"
            onClick={() => navigate(-1)}
            className="hover:bg-muted -ml-2 flex h-10 w-10 items-center justify-center rounded-lg transition-colors"
          >
            <ArrowLeft className="text-foreground h-5 w-5" />
          </button>
          <h1 className="text-foreground flex-1 text-center text-base font-semibold">Meu perfil</h1>
          <div className="h-10 w-10 shrink-0" aria-hidden="true" />
        </div>
      </header>

      <div className="container mx-auto max-w-6xl py-5 sm:py-7">
        <div className="grid gap-5 lg:grid-cols-[280px_minmax(0,1fr)] lg:gap-7">
          <ProfileSidebar
            activeTab={tab}
            user={user}
            displayName={displayName}
            contactLabel={contactLabel}
            counters={{
              bookings: bookingCounters.upcoming,
              favorites: totalFavorites,
              walkIns: walkInsQuery.counters.active,
            }}
            isLoggingOut={isLoggingOut}
            isUploadingPhoto={isUploadingPhoto}
            onChangeTab={changeTab}
            onFindPlaces={() => navigate('/')}
            onLogout={() => void handleLogout()}
            onOpenPhotoPicker={openPhotoPicker}
            photoInput={
              <input
                ref={photoInputRef}
                type="file"
                aria-label="Selecionar nova foto de perfil"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                className="sr-only"
                onChange={(event) => void handlePhotoSelection(event)}
              />
            }
          />

          <main className="min-w-0 space-y-5">
            {tab === 'details' && (
              <div className="grid gap-4 lg:grid-cols-[minmax(0,1.4fr)_minmax(280px,0.6fr)]">
                <Card className="border-border">
                  <CardHeader className="space-y-2">
                    <CardTitle>Seus dados</CardTitle>
                    <CardDescription>Edite suas informações para facilitar seus próximos agendamentos.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleSubmit(handleSaveProfile)} className="space-y-5">
                      <div className="space-y-1">
                        <Label htmlFor="profile-name">Nome</Label>
                        <Input
                          id="profile-name"
                          placeholder="Seu nome"
                          className="h-12"
                          {...register('name')}
                          disabled={isSubmitting || isUploadingPhoto}
                        />
                        <FormError message={errors.name?.message} />
                      </div>

                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-1">
                          <Label htmlFor="profile-email">Email</Label>
                          <Input
                            id="profile-email"
                            type="email"
                            placeholder="voce@exemplo.com"
                            className="h-12"
                            {...register('email')}
                            disabled={isSubmitting || isUploadingPhoto}
                          />
                          <FormError message={errors.email?.message} />
                        </div>

                        <div className="space-y-1">
                          <Label htmlFor="profile-phone">Telefone</Label>
                          <Input
                            id="profile-phone"
                            type="tel"
                            placeholder="(00) 00000-0000"
                            className="h-12"
                            {...phoneNumberField}
                            ref={(element) => {
                              phoneNumberField.ref(element);
                              phoneMaskRef(element);
                            }}
                            disabled={isSubmitting || isUploadingPhoto}
                          />
                          <FormError message={errors.phoneNumber?.message} />
                        </div>
                      </div>

                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-1">
                          <Label htmlFor="profile-birthDate">Data de nascimento</Label>
                          <Input
                            id="profile-birthDate"
                            type="date"
                            className="h-12"
                            {...register('birthDate')}
                            disabled={isSubmitting || isUploadingPhoto}
                          />
                          <FormError message={errors.birthDate?.message} />
                        </div>

                        <div className="space-y-1">
                          <Label htmlFor="profile-document">CPF</Label>
                          <Input
                            id="profile-document"
                            placeholder={canEditSocialNumber ? '000.000.000-00' : 'Não informado'}
                            className="h-12"
                            {...socialNumberField}
                            ref={(element) => {
                              socialNumberField.ref(element);
                              socialNumberMaskRef(element);
                            }}
                            disabled={isSubmitting || isUploadingPhoto || !canEditSocialNumber}
                            readOnly={!canEditSocialNumber}
                          />
                          <FormError message={errors.socialNumber?.message} />
                          <p className="text-muted-foreground text-xs">
                            {canEditSocialNumber
                              ? 'Você pode preencher o CPF enquanto ele ainda não estiver cadastrado.'
                              : 'O CPF já cadastrado não pode ser alterado por esta tela.'}
                          </p>
                        </div>
                      </div>

                      <div className="border-border flex flex-col gap-3 border-t pt-4 sm:flex-row">
                        <Button type="submit" className="h-11 sm:min-w-40" disabled={isSubmitting || isUploadingPhoto}>
                          {isSubmitting ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Salvando...
                            </>
                          ) : (
                            <>
                              <Save className="mr-2 h-4 w-4" />
                              Salvar dados
                            </>
                          )}
                        </Button>

                        <Button
                          type="button"
                          variant="outline"
                          className="h-11 sm:min-w-40"
                          onClick={() => reset(initialProfileValues)}
                          disabled={!isDirty || isSubmitting || isUploadingPhoto}
                        >
                          Desfazer
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>

                <div className="space-y-4">
                  <Card className="border-border">
                    <CardHeader className="space-y-2">
                      <CardTitle>Resumo da conta</CardTitle>
                      <CardDescription>Informações rápidas para acompanhar seu uso.</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="border-border bg-muted/40 flex items-start gap-3 rounded-2xl border p-4">
                        <Phone className="text-primary mt-0.5 h-4 w-4" />
                        <div>
                          <p className="text-foreground text-sm font-medium">Contato principal</p>
                          <p className="text-muted-foreground text-sm">{contactLabel}</p>
                        </div>
                      </div>

                      <div className="border-border bg-muted/40 flex items-start gap-3 rounded-2xl border p-4">
                        <Mail className="text-primary mt-0.5 h-4 w-4" />
                        <div>
                          <p className="text-foreground text-sm font-medium">Email</p>
                          <p className="text-muted-foreground text-sm">{user?.email || 'Não informado'}</p>
                        </div>
                      </div>

                      <div className="border-border bg-muted/40 flex items-start gap-3 rounded-2xl border p-4">
                        <CalendarClock className="text-primary mt-0.5 h-4 w-4" />
                        <div>
                          <p className="text-foreground text-sm font-medium">Próximo atendimento</p>
                          <p className="text-muted-foreground text-sm">
                            {nextBooking
                              ? `${nextBooking.companyName} · ${formatBookingDateLabel(nextBooking)} às ${nextBooking.time}`
                              : nextWalkIn
                                ? `Na fila em ${nextWalkIn.companyName}`
                                : 'Nenhum atendimento em andamento'}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}

            {tab === 'bookings' && (
              <div className="space-y-4">
                <Card className="border-border">
                  <CardContent className="flex flex-col gap-4 p-5 md:flex-row md:items-center md:justify-between">
                    <div>
                      <p className="text-primary text-sm font-medium">Fila presencial</p>
                      <h3 className="text-foreground text-xl font-bold">
                        {nextWalkIn
                          ? `${nextWalkIn.companyName}${nextWalkIn.protocol ? ` · ${nextWalkIn.protocol}` : ''}`
                          : 'Nenhuma fila ativa'}
                      </h3>
                      <p className="text-muted-foreground mt-1 text-sm">
                        {nextWalkIn
                          ? `Entrada registrada ${formatDateTimeLabel(nextWalkIn.createdAt)}.`
                          : 'Se você entrar pelo QR Code ou pela página do local, a fila aparece aqui.'}
                      </p>
                    </div>

                    <Button
                      type="button"
                      className="h-11 w-full md:w-auto md:min-w-44"
                      onClick={() => navigate(nextWalkIn?.companySlug ? `/${nextWalkIn.companySlug}` : '/')}
                    >
                      {nextWalkIn ? 'Ver local' : 'Encontrar locais'}
                    </Button>
                  </CardContent>
                </Card>

                {walkInsQuery.isLoading ? (
                  <Card className="border-border">
                    <CardContent className="text-muted-foreground flex items-center gap-3 p-6 text-sm">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Carregando sua fila presencial...
                    </CardContent>
                  </Card>
                ) : activeWalkIns.length === 0 ? (
                  <Card className="border-border">
                    <CardContent className="py-10 text-center">
                      <CalendarClock className="text-muted-foreground/30 mx-auto mb-3 h-10 w-10" />
                      <p className="text-foreground font-medium">Você não está em nenhuma fila presencial agora</p>
                      <p className="text-muted-foreground mt-1 text-sm">
                        Quando entrar em um Walk In, o acompanhamento e o cancelamento aparecem aqui.
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid gap-4">
                    {activeWalkIns.map((walkIn) => {
                      const walkInDisplayState = getProfileWalkInDisplayState(walkIn);
                      const canCancelWalkInEntry = canCancelProfileWalkIn(walkIn);
                      const isWalkInActionPending = walkInActionInFlight === walkIn.id;

                      return (
                        <Card key={walkIn.id} className="border-border hover:border-primary/30 transition-colors">
                          <CardContent className="space-y-4 p-5">
                            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                              <div>
                                <p className="text-foreground text-lg font-semibold">{walkIn.companyName}</p>
                                <p className="text-muted-foreground mt-1 text-sm">
                                  {walkIn.protocol ? `Protocolo ${walkIn.protocol}` : 'Fila presencial ativa'}
                                </p>
                              </div>

                              <span
                                className={`inline-flex w-fit rounded-full px-3 py-1 text-xs font-semibold ${
                                  walkInDisplayState === 'cancelled'
                                    ? 'bg-destructive/10 text-destructive'
                                    : walkInDisplayState === 'completed'
                                      ? 'bg-muted text-muted-foreground'
                                      : walkInDisplayState === 'called'
                                        ? 'bg-primary/10 text-primary'
                                        : 'bg-amber-100 text-amber-700'
                                }`}
                              >
                                {getProfileWalkInStatusLabel(walkIn)}
                              </span>
                            </div>

                            <div className="text-muted-foreground grid gap-3 text-sm md:grid-cols-2 xl:grid-cols-3">
                              <span className="flex items-center gap-2">
                                <CalendarClock className="text-primary h-4 w-4" />
                                {formatDateTimeLabel(walkIn.createdAt)}
                              </span>
                              <span className="flex items-center gap-2">
                                <Clock className="text-primary h-4 w-4" />
                                {typeof walkIn.position === 'number'
                                  ? `Posição ${walkIn.position}`
                                  : 'Aguardando chamada'}
                              </span>
                              <span className="flex items-center gap-2">
                                <MapPin className="text-primary h-4 w-4" />
                                {walkIn.companyName}
                              </span>
                            </div>

                            <div className="border-border flex flex-col gap-3 border-t pt-4 sm:flex-row">
                              <Button
                                type="button"
                                variant={canCancelWalkInEntry ? 'outline' : 'default'}
                                className="sm:min-w-40"
                                onClick={() => navigate(walkIn.companySlug ? `/${walkIn.companySlug}` : '/')}
                                disabled={isWalkInActionPending}
                              >
                                Ver local
                              </Button>

                              {canCancelWalkInEntry && (
                                <Button
                                  type="button"
                                  variant="outline"
                                  className="border-destructive/30 text-destructive hover:bg-destructive/5 hover:text-destructive sm:min-w-40"
                                  disabled={isWalkInActionPending}
                                  onClick={() => void handleCancelWalkIn(walkIn.id)}
                                >
                                  {isWalkInActionPending ? (
                                    <>
                                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                      Cancelando...
                                    </>
                                  ) : (
                                    'Sair da fila'
                                  )}
                                </Button>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}

                <Card className="border-border">
                  <CardContent className="flex flex-col gap-4 p-5 md:flex-row md:items-center md:justify-between">
                    <div>
                      <p className="text-primary text-sm font-medium">Seus horários</p>
                      <h3 className="text-foreground text-xl font-bold">
                        {nextBooking
                          ? `${nextBooking.companyName} · ${formatBookingDateLabel(nextBooking)}`
                          : 'Nenhum horário confirmado'}
                      </h3>
                      <p className="text-muted-foreground mt-1 text-sm">
                        {nextBooking
                          ? `${formatBookingSummary(nextBooking)} com ${nextBooking.professionalName}`
                          : 'Quando você confirmar um agendamento, ele aparece aqui.'}
                      </p>
                    </div>

                    <Button
                      type="button"
                      className="h-11 w-full md:w-auto md:min-w-44"
                      onClick={() => navigate(nextBooking?.companySlug ? `/${nextBooking.companySlug}` : '/')}
                    >
                      {nextBooking ? 'Ver local' : 'Agendar'}
                    </Button>
                  </CardContent>
                </Card>

                <div className="flex flex-col gap-2 sm:flex-row">
                  <button
                    type="button"
                    onClick={() => setBookingFilter('upcoming')}
                    className={`h-10 rounded-full px-4 text-sm font-medium transition-colors sm:w-auto ${
                      bookingFilter === 'upcoming'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Próximos ({bookingCounters.upcoming})
                  </button>
                  <button
                    type="button"
                    onClick={() => setBookingFilter('history')}
                    className={`h-10 rounded-full px-4 text-sm font-medium transition-colors sm:w-auto ${
                      bookingFilter === 'history'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Histórico ({bookingCounters.history})
                  </button>
                </div>

                {isLoadingVisibleBookings ? (
                  <Card className="border-border">
                    <CardContent className="text-muted-foreground flex items-center gap-3 p-6 text-sm">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Carregando seus agendamentos...
                    </CardContent>
                  </Card>
                ) : visibleBookings.length === 0 ? (
                  <Card className="border-border">
                    <CardContent className="py-14 text-center">
                      <Calendar className="text-muted-foreground/30 mx-auto mb-3 h-10 w-10" />
                      <p className="text-foreground font-medium">
                        {bookingFilter === 'upcoming'
                          ? 'Nenhum agendamento próximo'
                          : 'Nenhum agendamento no histórico'}
                      </p>
                      <p className="text-muted-foreground mt-1 text-sm">
                        {bookingFilter === 'upcoming'
                          ? 'Agende um horário para acompanhar tudo por aqui.'
                          : 'Os horários finalizados aparecem nesta aba.'}
                      </p>
                      {bookingFilter === 'upcoming' && (
                        <Button type="button" className="mt-5 h-11" onClick={() => navigate('/')}>
                          Agendar
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid gap-4">
                    {visibleBookings.map((booking) => {
                      const bookingDisplayState = getProfileBookingDisplayState(booking);
                      const canConfirm = canConfirmProfileBooking(booking);
                      const canCancel = canCancelProfileBooking(booking);
                      const isBookingActionPending = bookingActionInFlight?.bookingId === booking.id;
                      const totalPriceLabel =
                        typeof booking.totalPrice === 'number' ? currencyFormatter.format(booking.totalPrice) : null;

                      return (
                        <Card
                          key={booking.id}
                          role="button"
                          tabIndex={0}
                          aria-label={`Abrir agendamento ${booking.companyName}`}
                          className="border-border hover:border-primary/30 focus-visible:ring-primary cursor-pointer transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
                          onClick={() => openBookingManagement(booking.id)}
                          onKeyDown={(event) => handleBookingCardKeyDown(event, booking.id)}
                        >
                          <CardContent className="space-y-4 p-5">
                            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                              <div>
                                <p className="text-foreground text-lg font-semibold">{booking.companyName}</p>
                                <p className="text-muted-foreground mt-1 text-sm">{formatBookingSummary(booking)}</p>
                              </div>

                              <span
                                className={`inline-flex w-fit rounded-full px-3 py-1 text-xs font-semibold ${
                                  bookingDisplayState === 'cancelled'
                                    ? 'bg-destructive/10 text-destructive'
                                    : bookingDisplayState === 'confirmed'
                                      ? 'bg-primary/10 text-primary'
                                      : bookingDisplayState === 'pending'
                                        ? 'bg-amber-100 text-amber-700'
                                        : 'bg-muted text-muted-foreground'
                                }`}
                              >
                                {getProfileBookingStatusLabel(booking)}
                              </span>
                            </div>

                            <div className="text-muted-foreground grid gap-3 text-sm md:grid-cols-2 xl:grid-cols-4">
                              <span className="flex items-center gap-2">
                                <User className="text-primary h-4 w-4" />
                                {booking.professionalName}
                              </span>
                              <span className="flex items-center gap-2">
                                <Calendar className="text-primary h-4 w-4" />
                                {formatBookingDateLabel(booking)}
                              </span>
                              <span className="flex items-center gap-2">
                                <Clock className="text-primary h-4 w-4" />
                                {booking.time}
                              </span>
                              <span className="flex items-center gap-2">
                                <CalendarClock className="text-primary h-4 w-4" />
                                {totalPriceLabel || 'Valor a confirmar'}
                              </span>
                            </div>

                            <div className="border-border flex flex-col gap-3 border-t pt-4 sm:flex-row">
                              {canConfirm && (
                                <Button
                                  type="button"
                                  className="sm:min-w-40"
                                  disabled={isBookingActionPending}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    void handleConfirmBooking(booking.id);
                                  }}
                                >
                                  {isBookingActionPending && bookingActionInFlight?.action === 'confirm' ? (
                                    <>
                                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                      Confirmando...
                                    </>
                                  ) : (
                                    'Confirmar horário'
                                  )}
                                </Button>
                              )}

                              <Button
                                type="button"
                                variant={canConfirm ? 'outline' : 'default'}
                                className="sm:min-w-40"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  navigate(booking.companySlug ? `/${booking.companySlug}` : '/');
                                }}
                                disabled={isBookingActionPending}
                              >
                                {bookingFilter === 'upcoming' ? 'Ver local' : 'Agendar novamente'}
                              </Button>

                              {bookingDisplayState === 'completed' && (
                                <Button
                                  type="button"
                                  variant="outline"
                                  className="sm:min-w-40"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    navigate(`/agendamentos/${booking.id}/feedback`);
                                  }}
                                >
                                  <Star className="h-4 w-4" />
                                  Avaliar atendimento
                                </Button>
                              )}

                              {canCancel && (
                                <Button
                                  type="button"
                                  variant="outline"
                                  className="border-destructive/30 text-destructive hover:bg-destructive/5 hover:text-destructive sm:min-w-40"
                                  disabled={isBookingActionPending}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    void handleCancelBooking(booking.id);
                                  }}
                                >
                                  {isBookingActionPending && bookingActionInFlight?.action === 'cancel' ? (
                                    <>
                                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                      Cancelando...
                                    </>
                                  ) : (
                                    'Cancelar'
                                  )}
                                </Button>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {tab === 'favorites' && (
              <div>
                {favoriteShops.length === 0 ? (
                  <Card className="border-border">
                    <CardContent className="py-14 text-center">
                      <Heart className="text-muted-foreground/30 mx-auto mb-3 h-10 w-10" />
                      <p className="text-foreground font-medium">Nenhum favorito salvo</p>
                      <p className="text-muted-foreground mt-1 text-sm">
                        Toque no coração dos locais para montar sua lista.
                      </p>
                      <Button type="button" className="mt-5 h-11" onClick={() => navigate('/')}>
                        Ver horários
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid gap-4 md:grid-cols-2">
                    {favoriteShops.map((shop) => (
                      <Card key={shop.id} className="border-border overflow-hidden">
                        <div className="bg-muted relative h-40">
                          <img
                            src={resolveCompanyProfile(shop.profilePhoto)}
                            alt={getFavoriteShopName(shop)}
                            className="h-full w-full object-cover"
                          />
                        </div>

                        <CardContent className="space-y-4 p-5">
                          <div>
                            <p className="text-foreground text-lg font-semibold">{getFavoriteShopName(shop)}</p>
                            <p className="text-muted-foreground mt-1 flex items-center gap-2 text-sm">
                              <MapPin className="text-primary h-4 w-4" />
                              {shop.locationLabel || 'Local salvo na sua lista'}
                            </p>
                          </div>

                          <div className="flex flex-col gap-3 sm:flex-row">
                            <Button
                              type="button"
                              className="sm:min-w-40"
                              onClick={() => navigate(shop.slug ? `/${shop.slug}` : '/')}
                            >
                              Ver local
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              className="sm:min-w-40"
                              onClick={() => toggleFavorite(shop.id)}
                            >
                              Remover
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}

            {tab === 'preferences' && (
              <div className="space-y-5">
                <div>
                  <p className="text-primary text-sm font-semibold">Preferências</p>
                  <h2 className="text-foreground mt-1 text-2xl font-bold">Como você quer usar sua conta</h2>
                  <p className="text-muted-foreground mt-1 text-sm">
                    Ajuste seus canais de comunicação e as ações da conta em um só lugar.
                  </p>
                </div>

                {user ? <NotificationPreferencesPanel user={user} /> : null}

                <Card className="border-border lg:hidden">
                  <CardHeader>
                    <CardTitle>Ações da conta</CardTitle>
                    <CardDescription>Encontre novos locais ou encerre sua sessão neste dispositivo.</CardDescription>
                  </CardHeader>
                  <CardContent className="grid gap-3 sm:grid-cols-2">
                    <Button type="button" variant="outline" className="h-11" onClick={() => navigate('/')}>
                      <MapPin className="mr-2 h-4 w-4" /> Encontrar locais
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="border-destructive/30 text-destructive hover:bg-destructive/5 hover:text-destructive h-11"
                      onClick={() => void handleLogout()}
                      disabled={isLoggingOut}
                    >
                      {isLoggingOut ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <LogOut className="mr-2 h-4 w-4" />
                      )}
                      {isLoggingOut ? 'Saindo…' : 'Sair da conta'}
                    </Button>
                  </CardContent>
                </Card>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default Profile;
